import { WeatherOutlookDayResponse } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/responses/location/weather-outlook.response';
import { AdviceLunarResponse } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/responses/advice/advice-today.response';
import { ForecastMiniDay } from '../../../@commons/ui-components/forecast-mini/forecast-mini.component';

/** Le temps d'une journée, qualifié par le serveur (mêmes seuils que les conseils du jour). */
export type WeatherCondition = 'FROST' | 'HOT' | 'RAIN' | 'DRY' | 'WIND' | 'MILD';

/** Une journée de la bande : le mini des jours qui viennent, plus son temps et sa nuit. */
export interface BandDay extends ForecastMiniDay {
  /** Le jour, « AAAA-MM-JJ ». */
  date: string;
  condition: WeatherCondition;
  min: number | null;
}

/** La météo de la bande : aujourd'hui (pictogramme, nuit et jour), puis les jours suivants. */
export interface BandWeather {
  today: BandDay;
  /** Les jours suivants ; la bande en montre trois. Vide tant que le serveur ne les envoie pas. */
  next: BandDay[];
}

/** La lune de la bande, telle que `advice/today` la donne. */
export interface BandMoon {
  /** Phase (`MoonPhase` : WANING_GIBBOUS…). */
  phase: string;
  /** Type de jour : root, leaf, flower, fruit ; absent, la ligne dit la phase. */
  influence: string | null;
  /** Sens : montante ou descendante. */
  path: string | null;
}

/** La suite du temps en un mot : la clé (`home.band.weather.next.RAIN.day`), le jour qu'elle nomme, son temps et son pictogramme. */
export interface NextWeather {
  key: string;
  date: string;
  condition: WeatherCondition;
  /** Le pictogramme du jour annoncé (soleil demain, pluie mercredi), pas celui d'aujourd'hui. */
  icon: string;
}

/** Ce qui se dit avant un temps doux ; la sécheresse d'un jour se lit « doux ». */
const NOTABLE: readonly WeatherCondition[] = ['FROST', 'HOT', 'RAIN', 'WIND'];

/** Jours lus pour la suite du temps : aujourd'hui et les trois que la bande montre. */
const WINDOW = 4;

function word(condition: WeatherCondition): WeatherCondition {
  return NOTABLE.includes(condition) ? condition : 'MILD';
}

/**
 * La suite du temps en un mot (« Doux demain », « Pluie mercredi »), sans
 * jamais répéter ce que le message du jour dit déjà (`suppress`).
 *
 * Le gel se lit la nuit : celui d'un jour tombe la nuit d'avant (le gel de
 * demain matin est « cette nuit »), et celui d'aujourd'hui est déjà passé.
 *
 * - Le message parle déjà d'aujourd'hui ou de cette nuit (le gel) : on dit ce
 *   qui vient juste après, en sautant ce qu'il dit aussi (le vent de demain).
 * - Sinon, le premier temps marquant de la fenêtre (gel, chaleur, pluie,
 *   vent) ; à défaut, le temps de demain.
 */
export function nextWeather(weather: BandWeather, suppress: readonly WeatherCondition[] = []): NextWeather | null {
  const days = [weather.today, ...weather.next].slice(0, WINDOW);
  /** Le temps d'un jour, la nuit mise à part : un jour de gel déjà dit, ou passé, se lit doux. */
  const daytime = (day: BandDay, i: number): WeatherCondition =>
    day.condition === 'FROST' && (i === 0 || suppress.includes('FROST')) ? 'MILD' : word(day.condition);
  const said = (day: BandDay, i: number) => suppress.includes(daytime(day, i));
  const at = (index: number, condition: WeatherCondition): NextWeather => {
    // Le gel d'un jour est la nuit d'avant : celui de demain matin, c'est « cette nuit ».
    const offset = condition === 'FROST' ? index - 1 : index;
    const when = offset <= 0 ? 'today' : offset === 1 ? 'tomorrow' : 'day';
    return { key: `home.band.weather.next.${condition}.${when}`, date: days[condition === 'FROST' ? index - 1 : index].date, condition, icon: days[index].icon };
  };

  const tonight = days.slice(0, 2).some((day, i) => suppress.includes(word(day.condition)) && (day.condition !== 'FROST' || i === 1));
  if (tonight) {
    const after = days.findIndex((day, i) => i > 0 && !said(day, i));
    return after > 0 ? at(after, daytime(days[after], after)) : null;
  }
  const notable = days.findIndex((day, i) => NOTABLE.includes(daytime(day, i)) && !said(day, i));
  if (notable >= 0) return at(notable, daytime(days[notable], notable));
  const calm = days.findIndex((day, i) => i > 0 && !said(day, i));
  return calm > 0 ? at(calm, daytime(days[calm], calm)) : at(0, daytime(days[0], 0));
}

/** Les phases regroupées comme on les dit : « Lune décroissante » pour les deux décroissantes. */
export type MoonGroup = 'NEW' | 'WAXING' | 'FIRST_QUARTER' | 'FULL' | 'WANING' | 'LAST_QUARTER';

const MOON_GROUPS: Record<string, MoonGroup> = {
  NEW_MOON: 'NEW',
  WAXING_CRESCENT: 'WAXING',
  FIRST_QUARTER: 'FIRST_QUARTER',
  WAXING_GIBBOUS: 'WAXING',
  FULL_MOON: 'FULL',
  WANING_GIBBOUS: 'WANING',
  LAST_QUARTER: 'LAST_QUARTER',
  WANING_CRESCENT: 'WANING',
};

export function moonGroup(phase: string | null | undefined): MoonGroup | null {
  return (phase && MOON_GROUPS[phase]) || null;
}

/** Part éclairée de chaque phase, et son côté vu de l'hémisphère nord (croissante à droite). */
const MOON_LIGHT: Record<string, { lit: number; right: boolean }> = {
  NEW_MOON: { lit: 0, right: true },
  WAXING_CRESCENT: { lit: 0.25, right: true },
  FIRST_QUARTER: { lit: 0.5, right: true },
  WAXING_GIBBOUS: { lit: 0.75, right: true },
  FULL_MOON: { lit: 1, right: true },
  WANING_GIBBOUS: { lit: 0.75, right: false },
  LAST_QUARTER: { lit: 0.5, right: false },
  WANING_CRESCENT: { lit: 0.25, right: false },
};

/**
 * Le tracé de la partie éclairée, dans un disque de rayon 10,5 centré en
 * (12, 12) : le bord éclairé, puis le terminateur, une demi-ellipse. Vide pour
 * la nouvelle lune, le disque entier pour la pleine lune.
 */
export function moonLitPath(phase: string | null | undefined): string {
  const light = phase ? MOON_LIGHT[phase] : undefined;
  if (!light || light.lit === 0) return '';
  if (light.lit === 1) return 'M12 1.5a10.5 10.5 0 1 1 0 21a10.5 10.5 0 1 1 0-21z';
  const rx = Math.abs(1 - 2 * light.lit) * 10.5;
  const outer = light.right ? 1 : 0;
  // Croissant : le terminateur se creuse du côté éclairé ; gibbeuse : de l'autre.
  const inner = (light.lit > 0.5) === light.right ? 1 : 0;
  return `M12 1.5A10.5 10.5 0 0 ${outer} 12 22.5A${rx} 10.5 0 0 ${inner} 12 1.5z`;
}

const ICONS = {
  sun: '/assets/svg/weather/sun-colored.svg',
  heat: '/assets/svg/weather/sun-heat-colored.svg',
  partly: '/assets/svg/weather/cloudy-colored.svg',
  cloud: '/assets/svg/weather/cloud-colored.svg',
  rain: '/assets/svg/weather/cloud-rain-colored.svg',
  thunder: '/assets/svg/weather/cloud-thunder-colored.svg',
  wind: '/assets/svg/weather/cloud-wind-colored.svg',
  snow: '/assets/svg/weather/snow-colored.svg',
};

/** Codes de condition WeatherAPI (`conditionCode`) regroupés par pictogramme. */
const CODE_ICONS: [number[], string][] = [
  [[1000], ICONS.sun],
  [[1003], ICONS.partly],
  [[1006, 1009, 1030, 1135, 1147], ICONS.cloud],
  [[1087, 1273, 1276, 1279, 1282], ICONS.thunder],
  [[1066, 1069, 1072, 1114, 1117, 1204, 1207, 1210, 1213, 1216, 1219, 1222, 1225, 1237, 1249, 1252, 1255, 1258, 1261, 1264], ICONS.snow],
  [[1063, 1150, 1153, 1168, 1171, 1180, 1183, 1186, 1189, 1192, 1195, 1198, 1201, 1240, 1243, 1246], ICONS.rain],
];

/** Sans code de condition, le temps qualifié par le serveur donne le pictogramme. */
const CONDITION_ICONS: Record<WeatherCondition, string> = {
  FROST: ICONS.snow, HOT: ICONS.heat, RAIN: ICONS.rain, WIND: ICONS.wind, DRY: ICONS.sun, MILD: ICONS.partly,
};

/** Le pictogramme d'un jour : son code WeatherAPI, à défaut son temps. */
export function weatherIcon(code: number | null | undefined, condition: WeatherCondition): string {
  const found = code == null ? undefined : CODE_ICONS.find(([codes]) => codes.includes(code));
  return found?.[1] ?? CONDITION_ICONS[condition] ?? ICONS.partly;
}

function asCondition(value: string): WeatherCondition {
  return (['FROST', 'HOT', 'RAIN', 'DRY', 'WIND', 'MILD'] as const).find((c) => c === value) ?? 'MILD';
}

/**
 * La bande depuis `advice/today` : `forecast` donne aujourd'hui puis les trois
 * jours suivants, chacun qualifié par le serveur. Sans aujourd'hui, rien :
 * la bande dit « Pas à jour ».
 */
export function bandWeatherFromForecast(days: readonly WeatherOutlookDayResponse[] | null | undefined, today: Date = new Date()): BandWeather | null {
  const iso = today.toLocaleDateString('en-CA');
  const upcoming = (days ?? []).filter((d) => d.date >= iso);
  if (upcoming[0]?.date !== iso) return null;
  const [first, ...next] = upcoming.map((d): BandDay => {
    const condition = asCondition(d.condition);
    return { date: d.date, condition, icon: weatherIcon(d.conditionCode, condition), min: d.tempMin, max: d.tempMax };
  });
  return { today: first, next };
}

/** La lune du jour, calculée par le serveur avec les conseils. */
export function bandMoonFromLunar(lunar: AdviceLunarResponse | null | undefined): BandMoon | null {
  if (!lunar || !moonGroup(lunar.moonPhase)) return null;
  return { phase: lunar.moonPhase, influence: lunar.influence || null, path: lunar.moonPath || null };
}
